import cloudflareAssets from "@/assets/cloudflareAssets";
import { AttachmentType, BeneficiaryKeys } from "@/modules/beneficiary/common/enum";
import type {
  IBeneficiaryRequest,
  IOpenAttachmentDialogProps,
  TBeneficiaryAttachmentItem
} from "@/modules/beneficiary/common/types";
import { getAttachmentTypeOptions } from "@/modules/beneficiary/helpers";
import { useCreateBeneficiaryMutation } from "@/modules/beneficiary/services";
import { AppPaths, CloudflareAsset } from "@/shared/common/enums";
import { CloudFlareImage, Notification } from "@/shared/components";
import { useNotify } from "@/shared/hooks";
import { RadioGroup } from "@ab.uitools/ui-library";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { Image } from "@ab.uitools/ui-library/components/Image";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Switcher } from "@ab.uitools/ui-library/components/Switcher";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { type Dispatch, type SetStateAction, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import uuid4 from "uuid4";

import { AttachedItem } from "./AttachedItem";

interface Props {
  attachmentType: AttachmentType;
  onOpenAttachModal: (data?: IOpenAttachmentDialogProps) => void;
  setAttachmentType: Dispatch<SetStateAction<AttachmentType>>;
  accounts: TBeneficiaryAttachmentItem[];
  cards: TBeneficiaryAttachmentItem[];
  onDeleteAttached: (type: AttachmentType, index: number) => void;
}

export const CreateBeneficiaryForm = ({
  cards,
  accounts,
  attachmentType,
  onOpenAttachModal,
  setAttachmentType,
  onDeleteAttached,
}: Props) => {
  const { t } = useTranslation();
  const idempotencyKey = useRef(uuid4()).current;
  const navigate = useNavigate();
  const { isSubmitting, trigger, watch, setValue } = useFormProps();
  const {
    showErrorAlertNotification,
    showSuccessSnackbar,
  } = useNotify();

  const [createNewBeneficiary, { isLoading }] = useCreateBeneficiaryMutation();

  const values = watch?.();

  const onSubmit = () => {
    trigger?.().then((isValid) => {
      const accountEntries = accounts.map((item) => ({
        note: item.note,
        number: item.number,
        type: "Account",
      }));
      const cardEntries = cards.map((item) => ({
        note: item.note,
        number: item.number,
        type: "Card",
      }));
      if (isValid) {

        const data = {
          ...values,
          entries: [...accountEntries, ...cardEntries],
        } as IBeneficiaryRequest;

        createNewBeneficiary({ data, idempotencyKey })
          .unwrap()
          .then(() => {
            showSuccessSnackbar(t("alerts.beneficiaryHasBeenSuccessfullyCreated"));
            setTimeout(() => navigate(AppPaths.Beneficiaries));
          }).catch((error) => {
          showErrorAlertNotification(error);
          window.scrollTo({ top: 0, behavior: "smooth" });
        });
      }
    });
  };

  useEffect(() => {
    setValue(BeneficiaryKeys.Accounts, accounts);
  }, [accounts]);

  useEffect(() => {
    setValue(BeneficiaryKeys.Cards, cards);
  }, [cards]);

  return (
    <div className="full-width page-body">
      <div className="full-width flexbox">
        <div className="main-section">
          <div className="main-section-content">
            <Heading
              size="xsmall"
              weight="bolder"
              className="mb-32"
            >
              {t("beneficiary.createBeneficiary.title")}
            </Heading>
            <Notification/>
            <FormField
              name="name"
              className="mb-32 mt-16 beneficiary-name-input"
              errorMessageIcon="infoFilled"
              As={(props) => (
                <Input
                  {...props}
                  required
                  maxCount={50}
                  maxLength={50}
                  label={t("beneficiary.createBeneficiary.name")}
                  placeholder={t("beneficiary.createBeneficiary.enterName")}
                />
              )}
            />
            <div
              className="flexbox justify-content--between align-items--center beneficiary-switcher-wrapper">
              <div className="flex-direction--column">
                <Text type="primary" weight="bolder">
                  {t("beneficiary.createBeneficiary.isAvailableForOrgUsers")}
                </Text>
              </div>
              <FormField
                name="isAvailableForOrgUsers"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Switcher {...props}/>
                )}
              />
            </div>
            <div className="mt-32">
              <RadioGroup
                required
                label={t("beneficiary.attachmentType")}
                selected={attachmentType}
                handleChange={(value) => setAttachmentType(value as AttachmentType)}
                options={getAttachmentTypeOptions({ t })}
              />
            </div>
            <div
              className="attachment-area cursor-pointer mt-32"
              onClick={() => onOpenAttachModal()}
            >
              <Button
                className="attachment-button"
                iconProps={{
                  Component: (props) => (
                    <CloudFlareImage {...props} size="medium" path={CloudflareAsset.Add}/>
                  )
                }}
              />
              <Text
                weight="bold"
                size="medium"
                className="mt-24 mb-8"
              >
                {t(`beneficiary.attachment.${attachmentType}`)}
              </Text>
              <Text type="secondary" size="small">
                {t("beneficiary.attachment.description")}
              </Text>
            </div>
            {!!accounts.length && (
              <div className="flexbox flex-direction--column mt-32">
                <Text weight="bolder" size="large">
                  {t("general.accounts")}
                </Text>
                <div className="widget mt-20">
                  {accounts.map((account, index) => (
                    <div key={account.number} className="flexbox align-items--center pl-16 pr-16">
                      <div
                        className="card-account-image flexbox align-items--center justify-content--center mr-16">
                        <Image imagePath={cloudflareAssets.Account}/>
                      </div>
                      <AttachedItem
                        item={account}
                        index={index}
                        onEdit={(item, idx) => onOpenAttachModal({
                          type: AttachmentType.Account,
                          index: idx,
                          item,
                        })}
                        onDelete={(_, idx) => onDeleteAttached(AttachmentType.Account, idx)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!!cards.length && (
              <div className="flexbox flex-direction--column mt-32">
                <Text weight="bolder" size="large">
                  {t("general.cards")}
                </Text>
                <div className="widget mt-20">
                  {cards.map((card, index) => (
                    <div key={card.number} className="flexbox align-items--center pl-16 pr-16">
                      <div
                        className="card-account-image flexbox align-items--center justify-content--center mr-16">
                        <Image imagePath={card.network}/>
                      </div>
                      <AttachedItem
                        item={card}
                        index={index}
                        onEdit={(item, idx) => onOpenAttachModal({
                          type: AttachmentType.Card,
                          index: idx,
                          item,
                        })}
                        onDelete={(_, idx) => onDeleteAttached(AttachmentType.Card, idx)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="form-actions beneficiary-form-actions mt-32">
              <Button
                buttonActionType="button"
                buttonText={t("general.create")}
                type="primary"
                data-testid="create-beneficiary"
                data-ga-id="create-beneficiary"
                data-ga-action="Create beneficiary"
                onClick={onSubmit}
                disabled={isSubmitting || isLoading || (!accounts.length && !cards.length)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
